import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: colors.shadowCard };

const BillingManagement = () => {
  const [currentBilling, setCurrentBilling] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [billingData, historyData] = await Promise.all([
        api.admin.getCurrentBilling(),
        api.admin.getBillingHistoryAllBooks()
      ]);
      setCurrentBilling(billingData);
      setLedger(historyData.books || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (bookId) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(bookId) ? next.delete(bookId) : next.add(bookId);
    return next;
  });

  const setWeekPaid = async (record, paid) => {
    setBusyId(record.id);
    setError(null);
    try {
      if (paid) await api.admin.markBillingPaid(record.id);
      else await api.admin.markBillingUnpaid(record.id);
      setSuccess(paid ? 'Week marked paid' : 'Week marked unpaid');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update payment status');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return (
    <div style={{ color: colors.textPrimary, textAlign: 'center', padding: '3rem', fontFamily }}>
      Loading billing data...
    </div>
  );

  const books = currentBilling?.books || [];
  const period = currentBilling?.period;
  const currentTotal = books.reduce((s, b) => s + (b.total_amount || 0), 0);
  const totalOutstanding = ledger.reduce((s, b) => s + (b.unpaid_total || 0), 0);
  const wk = (p) => p ? `${new Date(p.period_start).toLocaleDateString()} – ${new Date(p.period_end).toLocaleDateString()}` : '—';

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <h2 style={{ color: colors.textPrimary, marginBottom: '1.25rem', fontSize: '1.3rem' }}>Billing Management</h2>

      {error && (
        <div style={{ background: 'rgba(220,53,69,0.1)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: 'rgba(40,167,69,0.1)', border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          {success}
        </div>
      )}

      {/* Current Period Header */}
      <div style={card}>
        <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: 4 }}>Current Billing Period</p>
        {period ? (
          <p style={{ fontWeight: 600, fontSize: '1rem' }}>
            {new Date(period.period_start).toLocaleDateString()} — {new Date(period.period_end).toLocaleDateString()}
            <span style={{ marginLeft: 8, fontSize: '0.75rem', padding: '2px 8px', borderRadius: 20, background: period.is_finalized ? 'rgba(108,117,125,0.2)' : 'rgba(40,167,69,0.2)', color: period.is_finalized ? colors.textFaint : colors.odds }}>
              {period.is_finalized ? 'Finalized' : 'In Progress'}
            </span>
          </p>
        ) : <p style={{ color: colors.textMuted }}>No active period</p>}
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: 4 }}>
          This week so far (all books): <span style={{ color: colors.gold, fontWeight: 700 }}>${currentTotal.toFixed(2)}</span>
        </p>
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: 4 }}>
          Total outstanding (all unpaid weeks): <span style={{ color: totalOutstanding > 0 ? colors.negative : colors.odds, fontWeight: 700 }}>${totalOutstanding.toFixed(2)}</span>
        </p>
        <p style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: 4 }}>
          Periods lock automatically every Sunday night — no action needed here.
        </p>
      </div>

      {/* Per-Book Current Usage */}
      <div style={card}>
        <h3 style={{ color: colors.textPrimary, marginBottom: '1rem', fontSize: '1rem' }}>This Week — Usage &amp; Rates (estimate)</h3>
        {books.length === 0 ? (
          <p style={{ color: colors.textMuted, textAlign: 'center', padding: '1.5rem' }}>No books found</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                  <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left', fontWeight: 'normal' }}>Book</th>
                  <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center', fontWeight: 'normal' }}>Active Users</th>
                  <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center', fontWeight: 'normal' }}>Live Users</th>
                  <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center', fontWeight: 'normal' }}>Rate/User</th>
                  <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center', fontWeight: 'normal' }}>This Week</th>
                </tr>
              </thead>
              <tbody>
                {books.map((book) => (
                  <tr key={book.book_id} style={{ borderBottom: `1px solid rgba(191,149,63,0.1)` }}>
                    <td style={{ padding: '0.6rem 0.5rem', color: colors.textPrimary, fontWeight: 600 }}>{book.book_name}</td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: colors.gold }}>{book.active_user_count || 0}</td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: colors.gold }}>{book.live_user_count || 0}</td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: colors.gold }}>${(book.rate_per_active_user || 0).toFixed(2)}</td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: colors.gold, fontWeight: 600 }}>${(book.total_amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Per-Book Ledger — past weeks + outstanding balance */}
      <div style={card}>
        <h3 style={{ color: colors.textPrimary, marginBottom: '0.5rem', fontSize: '1rem' }}>Outstanding Balances by Book</h3>
        <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '1rem' }}>
          Each book's balance is the sum of its unpaid weeks. Check a week off once the book has paid it — that week drops out of the balance; other unpaid weeks stay. A week that owed $0 counts as paid automatically.
        </p>
        {ledger.length === 0 ? (
          <p style={{ color: colors.textMuted, textAlign: 'center', padding: '1rem' }}>No locked billing weeks yet</p>
        ) : ledger.map((b) => {
          const open = expanded.has(b.book_id);
          return (
            <div key={b.book_id} style={{ border: `1px solid ${colors.goldMuted}`, borderRadius: 6, marginBottom: '0.6rem' }}>
              <button
                onClick={() => toggleExpand(b.book_id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'none', border: 'none', color: colors.textPrimary, cursor: 'pointer', fontFamily }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {open ? <ChevronDown size={16} color={colors.gold} /> : <ChevronRight size={16} color={colors.gold} />}
                  <span style={{ fontWeight: 600 }}>{b.book_name}</span>
                  {b.billing_enabled === false && (
                    <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 10, background: 'rgba(108,117,125,0.25)', color: colors.textFaint }}>billing off</span>
                  )}
                </span>
                <span style={{ fontWeight: 700, color: b.unpaid_total > 0 ? colors.negative : colors.odds }}>
                  ${b.unpaid_total.toFixed(2)} owed
                </span>
              </button>

              {open && (
                <div style={{ padding: '0 1rem 1rem', overflowX: 'auto' }}>
                  {b.records.length === 0 ? (
                    <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>No locked weeks yet.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                          <th style={{ color: colors.textMuted, padding: '0.4rem', textAlign: 'left', fontWeight: 'normal' }}>Week</th>
                          <th style={{ color: colors.textMuted, padding: '0.4rem', textAlign: 'center', fontWeight: 'normal' }}>Active</th>
                          <th style={{ color: colors.textMuted, padding: '0.4rem', textAlign: 'right', fontWeight: 'normal' }}>Amount</th>
                          <th style={{ color: colors.textMuted, padding: '0.4rem', textAlign: 'center', fontWeight: 'normal' }}>Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.records.map((r) => (
                          <tr key={r.id} style={{ borderBottom: `1px solid rgba(191,149,63,0.1)` }}>
                            <td style={{ padding: '0.5rem 0.4rem', color: colors.textPrimary }}>{wk(r.period)}</td>
                            <td style={{ padding: '0.5rem 0.4rem', textAlign: 'center', color: colors.textMuted }}>{r.active_user_count}</td>
                            <td style={{ padding: '0.5rem 0.4rem', textAlign: 'right', color: colors.gold, fontWeight: 600 }}>${r.total_amount.toFixed(2)}</td>
                            <td style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>
                              {r.auto_paid_zero ? (
                                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, background: `${colors.odds}22`, color: colors.odds }}>Paid ($0)</span>
                              ) : (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busyId === r.id ? 'wait' : 'pointer', opacity: busyId === r.id ? 0.5 : 1 }}>
                                  <input
                                    type="checkbox"
                                    checked={r.is_paid}
                                    disabled={busyId === r.id}
                                    onChange={(e) => setWeekPaid(r, e.target.checked)}
                                    style={{ accentColor: colors.gold, cursor: 'inherit' }}
                                  />
                                  <span style={{ color: r.is_paid ? colors.odds : colors.negative, fontSize: '0.75rem' }}>
                                    {r.is_paid ? 'Paid' : 'Unpaid'}
                                  </span>
                                </label>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BillingManagement;
